import React, { useMemo } from 'react';
import classnames from 'classnames';
import { get, noop, isEmpty, isString, head, isUndefined } from 'lodash';

// local dependencies
import { withNetworkState } from 'ui/hoc/withNetworkState';
import { getLabel } from 'utils/getLabel';
import { map, filter, includes, size } from 'lodash';
import { Icon } from 'modules/icon-bank';
import { radioBox as RadioBox } from 'ui/views/generic/radioBox';
import { dimensions as BASE_FIELD_WIDTH } from 'config/dimensions/member-auth-criteria-grid';
import { Typeahead } from 'modules/typeahead';
import { DATA_TYPES, BLANK_VALUE_SYMBOL } from 'constants/common';
import { Tooltip } from 'modules/tooltip';
import { button as Button } from 'modules/button';

/*******************************/

// `radio` field value view
const radioFieldValue = ( props ) => {

    // handle `address` value options
    if( 'address' === props.field.name ) {
        return (
            <div className='field-value-radio'>
                {
                    map( props.value, ( item ) => {
    
                        // get display title
                        const displayTitle = filter( [ item.line1, item.line2, item.city, item.state, item.zip ], Boolean ).join( ', ' );

                        // item class name
                        const itemClassName = classnames( 'field-value-radio__item', {
                            'field-value-radio__item--selected': props.selected === item.id,
                        } );
    
                        return (
                            <div key={ item.id } className={ itemClassName } onClick={ () => props.onSelect( item.id ) }>
                                {/* radio */}
                                <RadioBox name={ item.id } className='field-value-radio__item__checkbox' value={ item.id } selected={ props.selected === item.id } />
    
                                {/* display value */}
                                <div className='field-value-radio__item__title'>{ displayTitle }</div>
                            </div>
                        );
                    } )
                }
            </div>
        );
    }

    /*--------*/

    // normal `text` value options
    return (
        <div className='field-value-radio'>
            {
                map( props.value, ( item ) => {

                    // item class name
                    const itemClassName = classnames( 'field-value-radio__item', {
                        'field-value-radio__item--selected': props.selected === item,
                    } );
                    
                    return (
                        <div key={ item } className={ itemClassName } onClick={ () => props.onSelect( item ) }>
                            {/* radio */}
                            <RadioBox name={ item } className='field-value-radio__item__checkbox' value={ item } selected={ props.selected === item } />

                            {/* display value */}
                            <div className='field-value-radio__item__title'>{ item }</div>
                        </div>
                    );
                } )
            }
        </div>
    );
};

// set display name
radioFieldValue.displayName = 'authFlowMemberVerificationMatchCriteria.radioFieldValue';

// set prop types
radioFieldValue.defaultProps = {
    value: [],
    selected: null,
    field: {},
    onSelect: noop,
};

/*--------*/

// `search` field value view
const searchFieldValue = ( props ) => {
    return (
        <div className='field-value-search'>

            {/* typeahead */}
            <Typeahead
                className='field-value-search__typeahead'
                size='extra-small'
                url={ props.field.url }
                popupWidth={ 200 }
                requestPayload={ props.requestPayload }
                onChange={ ( { selected } ) => {
                    props.onSelect( head( selected ) ); // update selected value
                } }
            />

            {/* display selected value */}
            {
                isEmpty( props.selected ) ? null : (
                    <div className='field-value-search__selected'>
                        { props.selected }
                    </div>
                )
            }
        </div>
    );
};

// set display name
searchFieldValue.displayName = 'authFlowMemberVerificationMatchCriteria.searchFieldValue';

// set prop types
searchFieldValue.defaultProps = {
    value: [],
    field: {},
    requestPayload: {},
};

/*--------*/

// `text` field value view
const textFieldValue = ( props ) => {
    return (
        <div { ...props.binding } className='field-value-text'>
            { props.value }
        </div>
    );
};

// set display name
textFieldValue.displayName = 'authFlowMemberVerificationMatchCriteria.textFieldValue';

// set prop types
textFieldValue.defaultProps = {
    value: '',
    field: {},
};


// `dob` field value view
const dobFieldView = ( props ) => {

    const secondaryTitleKey = get( props.field, 'config.secondaryTitleKey' );
    const secondaryTitleValue = get( props.values, `${secondaryTitleKey}`, undefined );
    const secondaryTitle = get( props.field, 'config.secondaryTitle', undefined );

    if( isUndefined( secondaryTitleValue ) ) {
        return (
            <div { ...props.binding } className='field-value-text'>
                { props.value }
            </div>
        );
    } else {
        return (
            <div { ...props.binding } className='field-value-dob'>
                { props.value + ` (${ secondaryTitleValue } ${ getLabel( secondaryTitle ) })` }
            </div>
        );
    }

};

// set display name
dobFieldView.displayName = 'authFlowMemberVerificationMatchCriteria.dobFieldView';

// set prop types
dobFieldView.defaultProps = {
    value: '',
    field: {},
};


/*--------*/

// get field value view
const getFieldValueView = ( type ) => {
    switch( type ) {

        // radio selection
        case DATA_TYPES.RADIO : {
            return radioFieldValue;
        }

        // typeahead search
        case DATA_TYPES.SEARCH : {
            return searchFieldValue;
        }

        case DATA_TYPES.DOB: {
            return dobFieldView;
        }

        // text value
        default: {
            return textFieldValue;
        }
    }
};

/*******************************/

const CriteriaActionView = ( props ) => {

    const action = get( props, 'field.config.action', {} );

    return (
        <div className='auth-flow-criteria-field' style={ { width: BASE_FIELD_WIDTH[ props.field.name ] } }>

            {/* field title */}
            <div className='auth-flow-criteria-field__title'>
                { getLabel( props.field.title ) }
            </div>

            {/* action view */}
            <div className='auth-flow-criteria-field__selection'>
                <Button
                    { ...action }
                    size={ 'medium' }
                    type= { 'button' }
                    onClick={ () => props.onActionClick( { action, item: { memberId: props.memberId, patientId: props.patientId  } } ) }
                />
            </div>

        </div>
    );
};


/*******************************/

// criteria field view
const CriteriaFieldView = ( props ) => {

    // get field value and selected option value
    const fieldValue = props.values[ props.field.name ];

    const isDisabled = get( props.disabledFields, props.field.name, false );

    // `yes` and `no` action class name
    const yesActionClassName = classnames( 'auth-flow-criteria-field__selection__choice__item auth-flow-criteria-field__selection__choice__item--yes', {
        'auth-flow-criteria-field__selection__choice__item--active': true === get( props, 'status.result' ),
        'auth-flow-criteria-field__selection__choice__item--disabled': ( isEmpty( fieldValue ) && ! includes( [ DATA_TYPES.SEARCH, DATA_TYPES.RADIO ], props.field.type ) ) || isDisabled,
    } );
    const noActionClassName = classnames( 'auth-flow-criteria-field__selection__choice__item auth-flow-criteria-field__selection__choice__item--no', {
        'auth-flow-criteria-field__selection__choice__item--active': false === get( props, 'status.result' ),
        'auth-flow-criteria-field__selection__choice__item--disabled': ( isEmpty( fieldValue ) && ! includes( [ DATA_TYPES.SEARCH, DATA_TYPES.RADIO ], props.field.type ) ) || isDisabled,
    } );

    // get status result and value
    const statusResult = get( props, 'status.result', null );
    const statusValue = get( props, 'status.value', null );

    return (
        <div className='auth-flow-criteria-field' style={ { width: BASE_FIELD_WIDTH[ props.field.name ] } }>

            {/* field title */}
            <div className='auth-flow-criteria-field__title'>
                { getLabel( props.field.title ) }
            </div>

            {/* field value selection */}
            <div className='auth-flow-criteria-field__selection'>

                {/* choice container */}
                <div className='auth-flow-criteria-field__selection__choice'>
                    {/* check (yes) */}
                    <div
                        className={ yesActionClassName }
                        onClick={ !includes( [ DATA_TYPES.RADIO, DATA_TYPES.SEARCH ], props.field.type ) && !isEmpty( fieldValue ) && ! isDisabled ? () => props.onSetChoice( {
                            field: props.field,
                            value: isString( fieldValue ) ? fieldValue : null,
                            result: ( true === statusResult ? null : true ) }
                        ) : noop }
                    >
                        <Icon name='checkmark' fill='#ffffff' width='10' height='10'/>
                    </div>

                    {/* cross (no) */}
                    <div
                        className={ noActionClassName }
                        onClick={ ( isEmpty( fieldValue ) && ! includes( [ DATA_TYPES.SEARCH, DATA_TYPES.RADIO ], props.field.type ) ) || isDisabled? noop : () => props.onSetChoice( {
                            field: props.field,
                            value: isString( fieldValue ) ? fieldValue : null,
                            result: ( false === statusResult ? null : false )
                        } ) }
                    >
                        <Icon name='cross' fill='#ffffff' width='10' height='10'/>
                    </div>
                </div>

                {/* value container */}
                <div className='auth-flow-criteria-field__selection__value-container'>
                    {
                        ( ( isEmpty( fieldValue ) && ! includes( [ DATA_TYPES.SEARCH, DATA_TYPES.RADIO ], props.field.type ) ) || isDisabled ) ? (
                            BLANK_VALUE_SYMBOL
                        ) : (
                            <Tooltip
                                text={ fieldValue }
                                activateOnOverflow={ true }
                                render={ ( binding = {} ) => (
                                    <props.field.view
                                        binding= { binding }
                                        field={ props.field }
                                        value={ fieldValue }
                                        selected={ statusValue }
                                        onSelect={ ( value ) => props.onSetChoice( { field: props.field, value, result: true } ) }
                                        requestPayload={ props.requestPayload }
                                        values={ props.values }
                                    />
                                ) }
                            />
                        )
                    }
                </div>
            </div>
        </div>
    );
};

// set display name
CriteriaFieldView.displayName = 'authFlowMemberVerificationMatchCriteria.criteriaFieldView';

/*******************************/

// success view
export const authFlowMemberVerificationMatchCriteriaSuccess = ( props ) => {

    // dynamic widget class name
    const widgetClassName = classnames( 'ui-auth-flow-member-verification-match-criteria', {
        'ui-auth-flow-member-verification-match-criteria--verified': props.verified,
    } );

    /*-----------*/

    // cache critera (fields) and resolve views
    const fields = useMemo( () => {
        return map( props.criteria, field => {
            return( {
                ...field,
                view: getFieldValueView( field.type ),
            } );
        } );
    }, [] );

    /*-----------*/

    // mandatory and optional criteria fields
    const mandatoryFields = filter( fields, field => includes( props.mandatoryCriteria.ids, field.name ) );
    const optionalFields = filter( fields, field => includes( props.optionalCriteria.ids, field.name ) );

    /*-----------*/

    // mandatory and optional section classnames
    const mandatoryCriteriaSectionClassName = classnames( 'ui-auth-flow-member-verification-match-criteria__section ui-auth-flow-member-verification-match-criteria__section--mandatory', {
        'ui-auth-flow-member-verification-match-criteria__section--satisfied': props.criteriaStatus.mandatory,
    } );
    const optionalCriteriaSectionClassName = classnames( 'ui-auth-flow-member-verification-match-criteria__section ui-auth-flow-member-verification-match-criteria__section--optional', {
        'ui-auth-flow-member-verification-match-criteria__section--satisfied': props.criteriaStatus.optional,
    } );

    /*-----------*/

    return (
        <div className={ widgetClassName }>

            {/* mandatory section */}
            {
                !isEmpty( mandatoryFields ) ? (
                    <div className={ mandatoryCriteriaSectionClassName }>
                        {/* header */}
                        <div className='ui-auth-flow-member-verification-match-criteria__section__header'>
                            {/* title */}
                            <p className='ui-auth-flow-member-verification-match-criteria__section__header__title'>
                                { getLabel( props.mandatoryCriteria.title ) }
                            </p>
                        </div>

                        {/* field items */}
                        <div className='ui-auth-flow-member-verification-match-criteria__section__items'>
                            {
                                map( mandatoryFields, field => {
                                
                                    return (
                                        <CriteriaFieldView
                                            { ...props }
                                            key={ field.name }
                                            field={ field }
                                            status={ props.fieldsStatus[ field.name ] }
                                            requestPayload={ {
                                                context: {
                                                    memberId: props.memberId,
                                                    patientId: props.patientId
                                                }
                                            } }
                                        />
                                    );
                                } )
                            }
                        </div>
                    </div>
                ) : null
            }

            {/* optional section */}
            <div className={ optionalCriteriaSectionClassName }>
                {/* header */}
                <div className='ui-auth-flow-member-verification-match-criteria__section__header'>
                    {/* title */}
                    <p className='ui-auth-flow-member-verification-match-criteria__section__header__title'>
                        { getLabel( props.optionalCriteria.title ) } { props.minOptionalCriteriaMatch }/{ size( optionalFields ) }
                    </p>

                    {/* subtitle */}
                    <p className='ui-auth-flow-member-verification-match-criteria__section__header__title'>
                        { getLabel( props.optionalCriteria.subtitle ) }
                    </p>
                </div>

                {/* field items */}
                <div className='ui-auth-flow-member-verification-match-criteria__section__items'>
                    {
                        map( optionalFields, field => {

                            // check if the field has action and ahs visibility set to true
                            const actionField = get( field, 'config.action', {} );

                            if( ! isEmpty( actionField ) && get( props.actionVisibility, `${actionField.visibility}`, false ) ) {
                                return (
                                    <CriteriaActionView
                                        { ...props }
                                        key={ field.name }
                                        field={ field }
                                        status={ props.fieldsStatus[ field.name ] }
                                        requestPayload={ {
                                            context: {
                                                memberId: props.memberId,
                                                patientId: props.patientId
                                            }
                                        } }
                                        onActionClick={ props.onActionClick }
                                    />
                                );
                            }

                            return (
                                <CriteriaFieldView
                                    { ...props }
                                    key={ field.name }
                                    field={ field }
                                    status={ props.fieldsStatus[ field.name ] }
                                    requestPayload={ {
                                        context: {
                                            memberId: props.memberId,
                                        }
                                    } }
                                />
                            );
                        } )
                    }
                </div>
            </div>
        </div>
    );
};

// set display name
authFlowMemberVerificationMatchCriteriaSuccess.displayName = 'authFlowMemberVerificationMatchCriteriaSuccess';

// set default props
authFlowMemberVerificationMatchCriteriaSuccess.defaultProps = {
    criteria: [],
    fieldsStatus: {},
    criteriaStatus: {
        total: false,
        mandatory: false,
        optional: false,
    },
    values: {},
    mandatoryCriteria: {},
    optionalCriteria: {},
    actions: [],
    minOptionalCriteriaMatch: null,
    onSetChoice: noop,
    verified: false,
};

// prop types
authFlowMemberVerificationMatchCriteriaSuccess.propTypes = {};

/*******************************/

// preloader view
export const authFlowMemberVerificationMatchCriteriaPreloader = () => {
    return (
        <div className='ui-auth-flow-member-verification-match-criteria-preloader'>
            {/* left section */}
            {/* <div className='ui-auth-flow-member-verification-match-criteria-preloader__section ui-auth-flow-member-verification-match-criteria-preloader__section--left'>
                <div className='ui-auth-flow-member-verification-match-criteria-preloader__section__title' />
                <div className='ui-auth-flow-member-verification-match-criteria-preloader__section__head' />
                <div className='ui-auth-flow-member-verification-match-criteria-preloader__section__fields'>
                    {
                        map( [ 1, 2 ], item => {
                            return (
                                <div key={ item } className='ui-auth-flow-member-verification-match-criteria-preloader__section__fields__item' />
                            );
                        } )
                    }
                </div>
            </div> */}

            {/* right section */}
            <div className='ui-auth-flow-member-verification-match-criteria-preloader__section ui-auth-flow-member-verification-match-criteria-preloader__section--right'>
                <div className='ui-auth-flow-member-verification-match-criteria-preloader__section__title' />
                <div className='ui-auth-flow-member-verification-match-criteria-preloader__section__head' />
                <div className='ui-auth-flow-member-verification-match-criteria-preloader__section__fields'>
                    {
                        map( [ 1, 2, 3, 4, 5, 6, 7, 8, 9 ], item => {
                            return (
                                <div key={ item } className='ui-auth-flow-member-verification-match-criteria-preloader__section__fields__item' />
                            );
                        } )
                    }
                </div>
            </div>
        </div>
    );
};

// set display name
authFlowMemberVerificationMatchCriteriaPreloader.displayName = 'authFlowMemberVerificationMatchCriteriaPreloader';

// set default props
authFlowMemberVerificationMatchCriteriaPreloader.defaultProps = {};

// prop types
authFlowMemberVerificationMatchCriteriaPreloader.propTypes = {};

/*******************************/

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { authFlowMemberVerificationMatchCriteria as AuthFlowMemberVerificationMatchCriteria } from 'ui/views/widgets/authFlowMemberVerificationMatchCriteria';
 *
 * // render
 * <AuthFlowMemberVerificationMatchCriteria propName='propValue'/>
 */
export const authFlowMemberVerificationMatchCriteria = withNetworkState( authFlowMemberVerificationMatchCriteriaPreloader, authFlowMemberVerificationMatchCriteriaSuccess );
